import { useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, MailCheck, Send, XCircle } from 'lucide-react';
import { datumLang, stilHinweise, vorabBefund, type Vorlage } from '../../lib/mailBuilder/vorlagen';
import type { Segment } from '../../lib/supabase';
import { formatNumber } from '../../lib/format';
import type { MailUser } from '../../types';
import {
  ABSENDER_EMAIL, ABSENDER_NAME, empfaengerFehler, fragenFehler, inhaltsFehler, ortText,
  stichtagHinweis, type Stand, type VorlaufKontext,
} from './modell';

type Pruefpunkt = { id: string; titel: string; ok: boolean; warnung?: boolean; details: string[]; schritt: number };

function pruefpunkte(stand: Stand, v: Vorlage, html: string, gruppen: Segment[], vorlauf: VorlaufKontext): Pruefpunkt[] {
  const befund = vorabBefund(v, stand.antworten);
  // Beim Selbstversand gilt die Vorlauf-Regel der Redaktion nicht.
  const ff = fragenFehler(stand, v, vorlauf);
  const inh = inhaltsFehler(stand, v);
  const emp = empfaengerFehler(stand, gruppen);
  const stil = stilHinweise(stand.betreff, html);
  const tag = stichtagHinweis(stand, v);
  return [
    {
      id: 'fragen', schritt: 1, titel: 'Vorab-Fragen beantwortet, nichts mehr offen',
      ok: ff.length === 0 && befund.klaeren.length === 0,
      details: [...ff, ...befund.klaeren.map((k) => `Offen: ${k}`)],
    },
    { id: 'inhalt', schritt: 2, titel: 'Inhalt und Betreff vollständig', ok: inh.length === 0, details: inh },
    { id: 'empfaenger', schritt: 3, titel: 'Empfänger gewählt', ok: emp.length === 0, details: emp },
    {
      id: 'stil', schritt: 2, titel: stil.length || tag ? 'Hinweise der Redaktion beachtet' : 'Keine Stolpersteine gefunden',
      ok: true, warnung: stil.length > 0 || !!tag, details: [...stil, ...(tag ? [tag] : [])],
    },
  ];
}

type Props = {
  stand: Stand;
  v: Vorlage;
  html: string;
  user: MailUser | null;
  gruppen: Segment[];
  aendern: (teil: Partial<Stand>) => void;
  springen: (schritt: number) => void;
  geprueft: boolean;
  setGeprueft: (b: boolean) => void;
  testmail: (adresse: string) => Promise<void>;
  testGesendetAn: string | null;
  einreicher: boolean;
  zeigeFehler: boolean;
  /** Darf ohne Freigabe versenden — dann Versandzeitpunkt statt Nachricht an die Redaktion */
  selbst: boolean;
  /** Vorlauf-Regel: Eingang (nach Rückfrage das letzte Einreichen) und ob selbst versendet wird */
  vorlauf: VorlaufKontext;
  versandArt: 'wunsch' | 'sofort';
  setVersandArt: (a: 'wunsch' | 'sofort') => void;
  /** Wunschtermin liegt weit genug in der Zukunft, um einzuplanen */
  planbar: boolean;
};

export function SchrittPruefen({
  stand, v, html, user, gruppen, aendern, springen, geprueft, setGeprueft,
  testmail, testGesendetAn, einreicher, zeigeFehler, selbst, vorlauf, versandArt, setVersandArt, planbar,
}: Props) {
  const punkte = pruefpunkte(stand, v, html, gruppen, vorlauf);
  const befund = vorabBefund(v, stand.antworten);
  const gewaehlt = gruppen.filter((g) => stand.segmentIds.includes(g.id));
  const eigene = (user?.email ?? '').toLowerCase();
  const [adresse, setAdresse] = useState(/@randori-pro\.de$/.test(eigene) ? eigene : stand.kontaktEmail);
  const [sendet, setSendet] = useState(false);
  const [testFehler, setTestFehler] = useState<string | null>(null);

  const testSchicken = async () => {
    setTestFehler(null);
    const a = adresse.trim().toLowerCase();
    if (!/^[a-z0-9._%+-]+@randori-pro\.de$/.test(a)) {
      setTestFehler('Testmails gehen nur an Randori-Adressen (…@randori-pro.de).');
      return;
    }
    setSendet(true);
    try {
      await testmail(a);
    } catch (e) {
      setTestFehler((e as Error).message);
    } finally {
      setSendet(false);
    }
  };

  return (
    <div data-tour="assistent-arbeit">
      <section className="as-abschnitt">
        <h3 className="as-abschnitt-titel">Der Assistent hat geprüft</h3>
        <ul className="as-checkliste">
          {punkte.map((p) => (
            <li key={p.id} className={p.ok ? (p.warnung ? 'warn' : 'ok') : 'fehlt'}>
              {p.ok ? (p.warnung ? <AlertTriangle size={17} aria-hidden="true" /> : <CheckCircle2 size={17} aria-hidden="true" />) : <XCircle size={17} aria-hidden="true" />}
              <div>
                <strong>{p.titel}</strong>
                {p.details.length > 0 && <ul>{p.details.map((d) => <li key={d}>{d}</li>)}</ul>}
                {!p.ok && <button type="button" className="zf-aendern" onClick={() => springen(p.schritt)}>Zum Schritt</button>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="as-abschnitt">
        <h3 className="as-abschnitt-titel">Auf einen Blick</h3>
        <dl className="zusammenfassung">
          <div className="zf-zeile"><dt>Vorlage</dt><dd>{v.name}</dd><button type="button" className="zf-aendern" onClick={() => springen(0)}>ändern</button></div>
          <div className="zf-zeile"><dt>Sportschule</dt><dd>{ortText(stand) || '—'}</dd><button type="button" className="zf-aendern" onClick={() => springen(1)}>ändern</button></div>
          <div className="zf-zeile"><dt>Wunschtermin</dt><dd>{stand.wunschDatum ? `${datumLang(stand.wunschDatum)}, ${stand.wunschZeit} Uhr` : '—'}{stand.eilt && <> · <strong>eilt</strong>: {stand.eiltGrund}</>}</dd><button type="button" className="zf-aendern" onClick={() => springen(1)}>ändern</button></div>
          <div className="zf-zeile"><dt>Betreff</dt><dd>{stand.betreff || '—'}</dd><button type="button" className="zf-aendern" onClick={() => springen(2)}>ändern</button></div>
          <div className="zf-zeile"><dt>Vorschautext</dt><dd>{stand.vorschautext || '—'}</dd><button type="button" className="zf-aendern" onClick={() => springen(2)}>ändern</button></div>
          <div className="zf-zeile"><dt>Empfänger</dt><dd>{gewaehlt.length ? gewaehlt.map((g) => `${g.name} (${formatNumber(g.contact_count)})`).join(' · ') : '—'}{stand.zielgruppe.trim() && <><br /><em>Hinweis: {stand.zielgruppe}</em></>}</dd><button type="button" className="zf-aendern" onClick={() => springen(3)}>ändern</button></div>
          <div className="zf-zeile"><dt>Absender</dt><dd>{ABSENDER_NAME} &lt;{ABSENDER_EMAIL}&gt; · Fragen an {stand.kontaktEmail}</dd><span /></div>
        </dl>
      </section>

      <section className="as-abschnitt">
        <h3 className="as-abschnitt-titel"><MailCheck size={16} aria-hidden="true" /> Testmail an Dich</h3>
        <p className="as-warum">
          {einreicher && !selbst
            ? 'Die Testmail sieht aus wie die echte, trägt oben aber den Hinweis „noch nicht freigegeben". Schau sie Dir am besten auch auf dem Handy an.'
            : 'Die Testmail zeigt genau das, was verschickt wird. Schau sie Dir am besten auch auf dem Handy an — der Versand-Check fragt danach.'}
        </p>
        <div className="as-zeile as-zeile--knopf">
          <input className="form-input" type="email" value={adresse} onChange={(e) => setAdresse(e.target.value)} aria-label="Adresse für die Testmail" />
          <button type="button" className="btn btn-secondary" onClick={testSchicken} disabled={sendet}>
            {sendet ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
            {sendet ? 'Wird geschickt …' : 'Testmail schicken'}
          </button>
        </div>
        {testGesendetAn && <p className="as-ok" role="status"><CheckCircle2 size={15} aria-hidden="true" /> Testmail an {testGesendetAn} ist unterwegs.</p>}
        {testFehler && <p className="field-error" role="alert">{testFehler}</p>}
      </section>

      {selbst && (
        <section className="as-abschnitt" data-tour="assistent-versand">
          <h3 className="as-abschnitt-titel"><CalendarClock size={16} aria-hidden="true" /> Wann geht die Mail raus?</h3>
          <p className="as-warum">
            Du darfst ohne Freigabe versenden. Nach dem Versand-Check verschickt Brevo die Mail — zum Wunschtermin oder
            sofort. Möchtest Du lieber, dass die Redaktion noch einmal draufschaut, gib sie unten zur Freigabe.
          </p>
          <div className="as-optionen" role="radiogroup" aria-label="Versandzeitpunkt">
            <label className={`as-option${versandArt === 'wunsch' ? ' aktiv' : ''}${planbar ? '' : ' gesperrt'}`}>
              <input type="radio" name="versand-art" checked={versandArt === 'wunsch'} disabled={!planbar} onChange={() => setVersandArt('wunsch')} />
              <span>
                Zum Wunschtermin: {stand.wunschDatum ? `${datumLang(stand.wunschDatum)}, ${stand.wunschZeit} Uhr` : '—'}
                {!planbar && <em> (liegt zu nah oder in der Vergangenheit)</em>}
              </span>
            </label>
            <label className={`as-option${versandArt === 'sofort' ? ' aktiv' : ''}`}>
              <input type="radio" name="versand-art" checked={versandArt === 'sofort'} onChange={() => setVersandArt('sofort')} />
              <span>Sofort nach dem Check</span>
            </label>
          </div>
        </section>
      )}

      {einreicher && !selbst && (
        <section className="as-abschnitt">
          <h3 className="as-abschnitt-titel">Nachricht an die Redaktion (freiwillig)</h3>
          {befund.hinweise.length > 0 && (
            <div className="alert alert-warning">
              <AlertTriangle size={16} aria-hidden="true" />
              <div>
                <strong>Diese Hinweise aus den Vorab-Fragen gehen mit an die Redaktion:</strong>
                <ul className="as-fehlerliste">{befund.hinweise.map((h) => <li key={h}>{h}</li>)}</ul>
              </div>
            </div>
          )}
          <textarea className="form-textarea" rows={3} value={stand.nachricht} onChange={(e) => aendern({ nachricht: e.target.value })}
            placeholder="z. B. Bitte gern früher raus, falls es passt. Oder: Die Uhrzeit am Freitag bestätigt der Trainer noch bis morgen."
            aria-label="Nachricht an die Redaktion" />
        </section>
      )}

      <label className={`as-bestaetigen${zeigeFehler && !geprueft ? ' as-bestaetigen--fehlt' : ''}`}>
        <input type="checkbox" checked={geprueft} onChange={(e) => setGeprueft(e.target.checked)} />
        <span>Ich habe die Mail geprüft: Termine, Uhrzeiten, Links und Empfänger stimmen, und alles ist mit den Beteiligten abgestimmt.</span>
      </label>
      {zeigeFehler && !geprueft && <p className="field-error" role="alert">Bitte bestätigen.</p>}
    </div>
  );
}
